import {
  FEED_CLOSE,
  FEED_LIMITS,
  FEED_PROTOCOL,
  isNewerSeq,
  isSeq,
  parseServerMessage,
  type ClientMessage,
  type EventMessage,
  type FeedLotState,
  type FeedRole,
  type FeedSaleState,
  type LotStaffView,
  type ServerMessage,
  type SubscribeMessage,
  type TickerMessage,
  type YouMessage,
} from '@auctisync/engine-wire';

/**
 * The auction engine's live feed, protocol 2 — the ONE client for the website
 * and the bidder app (2026-10-08).
 *
 * Design: `docs/engine-live-work-design-2026-10-08.md` §2. Contract:
 * `@auctisync/engine-wire`, imported by the engine too, so the two sides
 * cannot drift. Jim, 2026-10-08: the engine owns everything live; the feed
 * sends only what changed, only to the people who need it.
 *
 * What a page gets from `createSaleFeed`:
 *   - a store of the sale's lots, keyed by lot id, kept current from the
 *     engine's `summary` (every lot, once) and `lot` messages (one changed lot,
 *     whole, ordered by `seq`);
 *   - the signed-in bidder's own standing per lot (`you`);
 *   - the sale-wide ticker (for a page that asks) and the bid-history events
 *     of the lots it is interested in;
 *   - a connection status a page can show: connecting | live | reconnecting |
 *     paused | closed.
 * It keeps the engine told which lots are ON SCREEN (`setView`) and WATCHED
 * (`setWatch`), pings, reconnects with back-off and replays what it missed.
 *
 * FRAMEWORK-NEUTRAL. No React, no DOM: the socket constructor and the clock
 * are injected, so the same code runs in a browser, in React Native and under
 * a fake clock in tests. Each app wraps it in its own hook; the hook holds no
 * rules of its own (memory: feedback_no_mechanical_divergence).
 *
 * NEVER THROWS INTO THE CALLER. Every failure — a token that would not come,
 * a socket that would not open, a message that would not parse, a listener
 * that threw — becomes a status or a report to `onError`. A live page must
 * not go blank because the network blinked.
 */

// ─── Public types ───────────────────────────────────────────────────────────

/**
 * One lot as the feed holds it.
 *
 * `detail: 'full'` is a whole `LotView` from a `lot` message. `detail:
 * 'summary'` is a line of the one-time `summary`, which carries fewer fields:
 * the fields it lacks are ABSENT (not known yet), never null — null keeps the
 * contract's meaning, "hidden from this audience, or none". A summary-only lot
 * becomes full when the engine sends its `lot` (it scrolled into view).
 */
export interface FeedLot {
  detail: 'summary' | 'full';
  lot_id: string;
  ordinal: number;
  state: FeedLotState;
  current_high_cents: number | null;
  bid_count: number | null;
  close_time: string | null;
  /** The engine sequence of the last change to this lot (decimal string). */
  seq: string;
  next_min_cents?: number | null;
  reserve_met?: boolean | null;
  last_bid_at?: string | null;
  staff?: LotStaffView;
}

/** A lot that changed, with what the feed held for it before (undefined = first sight). */
export interface FeedLotChange {
  lot: FeedLot;
  prev: FeedLot | undefined;
}

/** The bidder's own standing on one lot, from the engine's `you`. */
export type YouStanding = Omit<YouMessage, 'type'>;

/** One line of the sale-wide bid ticker. */
export type TickerEntry = Omit<TickerMessage, 'type'>;

export type SaleFeedStatus = 'connecting' | 'live' | 'reconnecting' | 'paused' | 'closed';

/**
 * Why a feed stopped for good. Only `closed_by_caller` is the page's doing;
 * the rest are answers retrying would not change, so the feed does not retry
 * (a hot loop against a full or newer engine helps nobody). A page that wants
 * to try again later creates a new feed.
 */
export type SaleFeedClosedReason =
  | 'closed_by_caller'
  /** The sale already holds the engine's maximum sockets. */
  | 'too_many_sockets'
  /** The engine no longer speaks protocol 2 — the page should reload. */
  | 'protocol_unsupported'
  /** The engine could not read what this client sent — a client bug. */
  | 'bad_message'
  /** No WebSocket implementation here, or it refused the engine's address. */
  | 'unavailable';

export interface SaleFeedStatusInfo {
  status: SaleFeedStatus;
  /**
   * When (device ms) the feed last lost — or has not yet had — a live socket;
   * null while live. A page stamps stale prices with it ("Prices as of 9:39").
   */
  downSince: number | null;
  /** Set only when `status` is `closed`. */
  closedReason: SaleFeedClosedReason | null;
}

export interface SaleFeedEvents {
  /** Lots that changed in one message — one entry for a `lot`, many for a `summary`. */
  lots: (changes: readonly FeedLotChange[]) => void;
  you: (standing: YouStanding, prev: YouStanding | undefined) => void;
  ticker: (entry: TickerEntry) => void;
  event: (event: EventMessage) => void;
  status: (info: SaleFeedStatusInfo) => void;
  sale: (state: FeedSaleState) => void;
}

/** What `setView` / `setWatch` did. */
export interface InterestUpdate {
  /** The set differs from the one before (a message went out if the socket is up). */
  changed: boolean;
  /** The ids kept, in wire order. */
  lots: readonly string[];
  /** Ids beyond the cap, in the caller's order — see `normalizeInterest`. */
  dropped: readonly string[];
}

/**
 * The slice of a WebSocket the feed uses: `send`, `close` and the four `on*`
 * handlers, which the browser's `WebSocket` (proved at compile time in the
 * tests) and React Native's both provide; so does a fake. Handlers are
 * declared bivariant so a platform socket's richer event types are accepted.
 */
type Handler<E> = { bivarianceHack(event: E): void }['bivarianceHack'];
export interface FeedSocket {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: Handler<unknown> | null;
  onmessage: Handler<{ data: unknown }> | null;
  onclose: Handler<{ code: number; reason?: string }> | null;
  onerror: Handler<unknown> | null;
}
export type FeedSocketConstructor = new (url: string) => FeedSocket;

/** Time, injected. Defaults to the platform's own timers and `Date.now`. */
export interface FeedClock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
}

/**
 * How long the socket may be down, while the sale is running, before the feed
 * says `paused` ("Bidding paused — your bids are safe").
 *
 * 30 s matches the engine's provisional outage threshold (design §7). Jim,
 * 2026-10-08, on both of the engine's outage numbers: *"We won't know what is
 * appropriate until we have stress tested many more times."* So it is a
 * DEFAULT, overridable per feed (`pausedAfterMs`), and moves with the engine's.
 */
export const DEFAULT_FEED_PAUSED_AFTER_MS = 30_000;

/** First reconnect wait, before jitter; doubles per failed attempt up to the max. */
export const DEFAULT_FEED_BACKOFF_INITIAL_MS = 500;
export const DEFAULT_FEED_BACKOFF_MAX_MS = 30_000;
/** A connection live this long has proved itself: the next drop starts back-off afresh. */
export const DEFAULT_FEED_STABLE_AFTER_MS = 10_000;
/** No answer to a ping (or anything else) in this long = the socket is dead. */
export const DEFAULT_FEED_PONG_TIMEOUT_MS = 10_000;
/**
 * A socket whose subscription the engine has not confirmed (`subscribed`) in
 * this long is abandoned and retried — whether it never opened, or opened onto
 * an engine that will not take the subscription (one that predates protocol 2
 * answers `invalid_message` and would otherwise hold the feed in `connecting`).
 */
export const DEFAULT_FEED_CONNECT_TIMEOUT_MS = 15_000;

export interface SaleFeedOptions {
  /** The engine's base URL, from the bid-path lookup (`resolveLiveSource`) — never baked in. */
  engineUrl: string;
  engineAuctionId: string;
  houseId: string;
  /**
   * The bidder's access token, or null when signed out (a public observer).
   * Asked on EVERY connect, so a reconnect carries a refreshed token. A getter
   * that rejects is a failed connect, retried — never a silent downgrade to a
   * public observer, which would lose the bidder's own standing.
   */
  getToken: () => Promise<string | null>;
  /** Lot ids on screen, in on-screen priority order (the cap keeps the first). */
  view?: readonly string[] | undefined;
  /** Lot ids the viewer watches in this sale. */
  watch?: readonly string[] | undefined;
  /** The sale-wide bid ticker (the Lots page). */
  ticker?: boolean | undefined;
  /** One `summary` of every lot on each (re)subscribe (list pages). */
  summary?: boolean | undefined;
  /**
   * The sale's state as the page already knows it, until the engine says.
   * Decides whether a long drop reads as `paused`. Null = unknown: never paused.
   */
  initialSaleState?: FeedSaleState | null | undefined;
  /** See `DEFAULT_FEED_PAUSED_AFTER_MS`. */
  pausedAfterMs?: number | undefined;
  pingIntervalMs?: number | undefined;
  pongTimeoutMs?: number | undefined;
  connectTimeoutMs?: number | undefined;
  backoff?: { initialMs?: number | undefined; maxMs?: number | undefined } | undefined;
  stableAfterMs?: number | undefined;
  /** Defaults to the platform's global `WebSocket`. */
  WebSocket?: FeedSocketConstructor | undefined;
  clock?: FeedClock | undefined;
  /** Jitter source in [0, 1). Injected so tests are deterministic. */
  random?: (() => number) | undefined;
  /**
   * Problems that are not a status: a listener that threw, a message the feed
   * could not read, an engine `error`. Defaults to the platform's
   * `reportError`, else `console.error` — surfaced, never swallowed.
   */
  onError?: ((error: unknown) => void) | undefined;
}

export interface SaleFeed {
  /** Listen; returns the unsubscribe. */
  on<K extends keyof SaleFeedEvents>(event: K, listener: SaleFeedEvents[K]): () => void;
  /** Replace the on-screen set. Sends only when the set actually changed. */
  setView(lotIds: readonly string[]): InterestUpdate;
  /** Replace the watched set. Sends only when the set actually changed. */
  setWatch(lotIds: readonly string[]): InterestUpdate;
  lot(lotId: string): FeedLot | undefined;
  lots(): ReadonlyMap<string, FeedLot>;
  /** The bidder's own standing on a lot; undefined when they hold none (or are signed out). */
  you(lotId: string): YouStanding | undefined;
  yours(): ReadonlyMap<string, YouStanding>;
  status(): SaleFeedStatusInfo;
  saleState(): FeedSaleState | null;
  /** Who the engine bound this socket as; null before its `ack`. */
  role(): FeedRole | null;
  /** Engine clock minus device clock (ms), from the `ack`; null before one. */
  serverOffsetMs(): number | null;
  /** Stop for good. Idempotent. */
  close(): void;
}

// ─── Pure helpers (exported for the tests and for the apps' hooks) ─────────

/**
 * The interest rule, for `view` and `watch` alike:
 *   1. drop non-strings, empties and repeats, keeping the FIRST occurrence;
 *   2. keep the first `cap` in the CALLER'S order — callers pass on-screen
 *      lots top to bottom, so what is dropped is what is furthest down;
 *   3. sort what is kept, so the same set is always the same message and a
 *      re-render that merely reorders sends nothing.
 */
export function normalizeInterest(
  lotIds: readonly unknown[],
  cap: number,
): { lots: string[]; dropped: string[] } {
  const seen = new Set<string>();
  const kept: string[] = [];
  const dropped: string[] = [];
  for (const id of lotIds) {
    if (typeof id !== 'string' || id.length === 0 || seen.has(id)) continue;
    seen.add(id);
    if (kept.length < cap) kept.push(id);
    else dropped.push(id);
  }
  kept.sort();
  return { lots: kept, dropped };
}

/**
 * Reconnect wait for the `attempt`-th consecutive failure (0-based): the base
 * doubles from `initialMs` up to `maxMs`, and the wait is half the base plus a
 * random half ("equal jitter") — never zero, and spread so thousands of
 * sockets dropped together (Stress 1 saw four mass drops) do not return in
 * the same instant.
 */
export function feedBackoffMs(
  attempt: number,
  initialMs: number,
  maxMs: number,
  random: () => number,
): number {
  const base = Math.min(maxMs, initialMs * 2 ** Math.max(0, attempt));
  const r = Math.min(Math.max(random(), 0), 1);
  return base / 2 + r * (base / 2);
}

/**
 * The engine socket's address. `https` becomes `wss`. The token rides the
 * query string because a browser cannot set headers on a WebSocket (the
 * engine reads `?token=`); a signed-out visitor sends none and is bound as a
 * public observer.
 */
export function feedSocketUrl(
  engineUrl: string,
  engineAuctionId: string,
  houseId: string,
  token: string | null,
): string {
  const base = engineUrl
    .replace(/\/+$/, '')
    .replace(/^http(s?):\/\//i, (_m, s: string) => `ws${s}://`);
  const query = [`house=${encodeURIComponent(houseId)}`];
  if (token !== null && token.length > 0) query.push(`token=${encodeURIComponent(token)}`);
  return `${base}/v1/ws/auction/${encodeURIComponent(engineAuctionId)}?${query.join('&')}`;
}

/** A sale whose lots can be bid on — a drop during one is worth saying `paused` about. */
export function isSaleRunning(state: FeedSaleState | null): boolean {
  return state === 'live' || state === 'closing' || state === 'paused';
}

// ─── Validation of what arrives inside a message ───────────────────────────
// `parseServerMessage` checks a message's envelope; these check the payload
// the store keeps, so a malformed line is skipped rather than poisoning it.

const LOT_STATES: Record<FeedLotState, true> = {
  pending: true,
  open: true,
  bin_pending: true,
  closing: true,
  closed_sold: true,
  closed_no_winner: true,
  closed_bin_sold: true,
  closed_no_sale: true,
  canceled: true,
};
const SALE_STATES: Record<FeedSaleState, true> = {
  scheduled: true,
  live: true,
  closing: true,
  closed: true,
  paused: true,
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
// `Object.hasOwn`, not `in`: `'constructor' in {}` is true.
const isLotState = (v: unknown): v is FeedLotState =>
  typeof v === 'string' && Object.hasOwn(LOT_STATES, v);
const isSaleState = (v: unknown): v is FeedSaleState =>
  typeof v === 'string' && Object.hasOwn(SALE_STATES, v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const numOrNull = (v: unknown): v is number | null => v === null || isNum(v);
const strOrNull = (v: unknown): v is string | null => v === null || typeof v === 'string';
const boolOrNull = (v: unknown): v is boolean | null => v === null || typeof v === 'boolean';

function readSummaryLine(raw: unknown): FeedLot | null {
  if (!isRecord(raw)) return null;
  const r = raw;
  if (
    typeof r.lot_id !== 'string' ||
    r.lot_id.length === 0 ||
    !isSeq(r.seq) ||
    !isNum(r.ordinal) ||
    !isLotState(r.state) ||
    !numOrNull(r.current_high_cents) ||
    !numOrNull(r.bid_count) ||
    !strOrNull(r.close_time)
  ) {
    return null;
  }
  return {
    detail: 'summary',
    lot_id: r.lot_id,
    ordinal: r.ordinal,
    state: r.state,
    current_high_cents: r.current_high_cents,
    bid_count: r.bid_count,
    close_time: r.close_time,
    seq: r.seq,
  };
}

function readStaff(raw: unknown): LotStaffView | null {
  if (!isRecord(raw)) return null;
  const r = raw;
  if (
    !strOrNull(r.leader_pseudonym) ||
    !numOrNull(r.second_high_cents) ||
    !numOrNull(r.reserve_cents) ||
    !isNum(r.ceiling_count)
  ) {
    return null;
  }
  return {
    leader_pseudonym: r.leader_pseudonym,
    second_high_cents: r.second_high_cents,
    reserve_cents: r.reserve_cents,
    ceiling_count: r.ceiling_count,
  };
}

function readLotView(raw: unknown): FeedLot | null {
  const line = readSummaryLine(raw);
  if (line === null || !isRecord(raw)) return null;
  const r = raw;
  if (!numOrNull(r.next_min_cents) || !boolOrNull(r.reserve_met) || !strOrNull(r.last_bid_at)) {
    return null;
  }
  const lot: FeedLot = {
    ...line,
    detail: 'full',
    next_min_cents: r.next_min_cents,
    reserve_met: r.reserve_met,
    last_bid_at: r.last_bid_at,
  };
  if (r.staff !== undefined) {
    // Staff detail that does not read is refused with the view: a house
    // console showing a half-read reserve is worse than one a beat behind.
    const staff = readStaff(r.staff);
    if (staff === null) return null;
    lot.staff = staff;
  }
  return lot;
}

function readYou(m: Record<string, unknown>): YouStanding | null {
  if (
    !isSeq(m.seq) ||
    typeof m.lot_id !== 'string' ||
    typeof m.leading !== 'boolean' ||
    !numOrNull(m.ceiling_cents) ||
    typeof m.outbid !== 'boolean'
  ) {
    return null;
  }
  return {
    seq: m.seq,
    lot_id: m.lot_id,
    leading: m.leading,
    ceiling_cents: m.ceiling_cents,
    outbid: m.outbid,
  };
}

function readTicker(m: Record<string, unknown>): TickerEntry | null {
  if (
    !isSeq(m.seq) ||
    typeof m.lot_id !== 'string' ||
    !isNum(m.ordinal) ||
    !isNum(m.amount_cents) ||
    typeof m.at !== 'string'
  ) {
    return null;
  }
  return {
    seq: m.seq,
    lot_id: m.lot_id,
    ordinal: m.ordinal,
    amount_cents: m.amount_cents,
    at: m.at,
  };
}

/** Insertion-ordered set that forgets its oldest keys past `max` — replay de-duplication. */
class RecentKeys {
  private readonly keys = new Set<string>();
  constructor(private readonly max: number) {}
  /** True when `key` is new (and records it). */
  add(key: string): boolean {
    if (this.keys.has(key)) return false;
    this.keys.add(key);
    if (this.keys.size > this.max) {
      const oldest = this.keys.values().next().value;
      if (oldest !== undefined) this.keys.delete(oldest);
    }
    return true;
  }
}

const sameList = (a: readonly string[] | null, b: readonly string[]): boolean =>
  a !== null && a.length === b.length && a.every((x, i) => x === b[i]);

const systemClock: FeedClock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (h) => clearInterval(h as ReturnType<typeof setInterval>),
};

function defaultReportError(error: unknown): void {
  const report = (globalThis as { reportError?: unknown }).reportError;
  if (typeof report === 'function') {
    (report as (e: unknown) => void)(error);
    return;
  }
  console.error(error);
}

function platformWebSocket(): FeedSocketConstructor | undefined {
  const ws = (globalThis as { WebSocket?: unknown }).WebSocket;
  return typeof ws === 'function' ? (ws as FeedSocketConstructor) : undefined;
}

const PING: ClientMessage = { type: 'ping' };
/** How many ticker lines / events the feed remembers to drop replayed copies. */
const REPLAY_MEMORY = 2000;

// ─── The feed ───────────────────────────────────────────────────────────────

export function createSaleFeed(options: SaleFeedOptions): SaleFeed {
  const clock = options.clock ?? systemClock;
  const random = options.random ?? Math.random;
  const reportError = options.onError ?? defaultReportError;
  const pausedAfterMs = options.pausedAfterMs ?? DEFAULT_FEED_PAUSED_AFTER_MS;
  const pingIntervalMs = options.pingIntervalMs ?? FEED_LIMITS.PING_INTERVAL_MS;
  const pongTimeoutMs = options.pongTimeoutMs ?? DEFAULT_FEED_PONG_TIMEOUT_MS;
  const connectTimeoutMs = options.connectTimeoutMs ?? DEFAULT_FEED_CONNECT_TIMEOUT_MS;
  const backoffInitialMs = options.backoff?.initialMs ?? DEFAULT_FEED_BACKOFF_INITIAL_MS;
  const backoffMaxMs = options.backoff?.maxMs ?? DEFAULT_FEED_BACKOFF_MAX_MS;
  const stableAfterMs = options.stableAfterMs ?? DEFAULT_FEED_STABLE_AFTER_MS;
  const wantsTicker = options.ticker ?? false;
  const wantsSummary = options.summary ?? false;

  const listeners: { [K in keyof SaleFeedEvents]: Set<SaleFeedEvents[K]> } = {
    lots: new Set(),
    you: new Set(),
    ticker: new Set(),
    event: new Set(),
    status: new Set(),
    sale: new Set(),
  };

  // The store.
  const lots = new Map<string, FeedLot>();
  const yours = new Map<string, YouStanding>();
  const seenTicker = new RecentKeys(REPLAY_MEMORY);
  const seenEvents = new RecentKeys(REPLAY_MEMORY);
  /** The highest sequence applied — sent as `replay_from_seq` (inclusive) on reconnect. */
  let cursor: string | null = null;
  let saleState: FeedSaleState | null = options.initialSaleState ?? null;
  let saleSeq: string | null = null;
  let role: FeedRole | null = null;
  let offsetMs: number | null = null;

  // Interest: what the page wants, and what the current socket was last told.
  let desiredView = normalizeInterest(options.view ?? [], FEED_LIMITS.MAX_VIEW_LOTS).lots;
  let desiredWatch = normalizeInterest(options.watch ?? [], FEED_LIMITS.MAX_WATCH_LOTS).lots;
  let sentView: string[] | null = null;
  let sentWatch: string[] | null = null;

  // Connection.
  let status: SaleFeedStatus = 'connecting';
  let closedReason: SaleFeedClosedReason | null = null;
  let downSince: number | null = clock.now();
  let socket: FeedSocket | null = null;
  let subscribeSent = false; // on the current socket
  let liveSince: number | null = null;
  let attempt = 0;
  let connectGeneration = 0;
  let reconnectTimer: unknown = null;
  let pauseTimer: unknown = null;
  let pingTimer: unknown = null;
  let pongTimer: unknown = null;
  let connectTimer: unknown = null;
  // Server-clock sampling for the current socket: the `ack`, until the first
  // round trip after it has refined the offset; and the unanswered ping.
  let ackSample: { serverTime: number; receivedAt: number } | null = null;
  let pingSentAt: number | null = null;

  // ── emit ──
  function emit<K extends keyof SaleFeedEvents>(
    event: K,
    ...args: Parameters<SaleFeedEvents[K]>
  ): void {
    for (const listener of [...listeners[event]]) {
      try {
        (listener as (...a: Parameters<SaleFeedEvents[K]>) => void)(...args);
      } catch (error) {
        reportError(error);
      }
    }
  }

  const statusInfo = (): SaleFeedStatusInfo => ({ status, downSince, closedReason });

  function setStatus(next: SaleFeedStatus): void {
    if (status === next) return;
    status = next;
    emit('status', statusInfo());
  }

  function send(message: ClientMessage): boolean {
    if (socket === null) return false;
    try {
      socket.send(JSON.stringify(message));
      return true;
    } catch (error) {
      reportError(error);
      return false;
    }
  }

  function noteSeq(seq: string): void {
    if (isNewerSeq(seq, cursor)) cursor = seq;
  }

  // ── timers ──
  function clearTimer(handle: unknown, interval = false): null {
    if (handle !== null) {
      if (interval) clock.clearInterval(handle);
      else clock.clearTimeout(handle);
    }
    return null;
  }

  function clearSocketTimers(): void {
    pingTimer = clearTimer(pingTimer, true);
    pongTimer = clearTimer(pongTimer);
    connectTimer = clearTimer(connectTimer);
  }

  /** `paused` = down longer than the threshold while the sale is running. */
  function evaluatePause(): void {
    pauseTimer = clearTimer(pauseTimer);
    if (status === 'closed' || status === 'live' || downSince === null) return;
    if (!isSaleRunning(saleState)) return;
    const remaining = downSince + pausedAfterMs - clock.now();
    if (remaining <= 0) {
      setStatus('paused');
      return;
    }
    pauseTimer = clock.setTimeout(() => {
      pauseTimer = null;
      evaluatePause();
    }, remaining);
  }

  // ── socket lifecycle ──
  function detachSocket(): void {
    const ws = socket;
    socket = null;
    subscribeSent = false;
    sentView = null;
    sentWatch = null;
    ackSample = null;
    pingSentAt = null;
    clearSocketTimers();
    if (ws === null) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try {
      ws.close(1000, 'client');
    } catch {
      // Already closed, or never opened — either way it is gone.
    }
  }

  function terminate(reason: SaleFeedClosedReason): void {
    if (status === 'closed') return;
    connectGeneration += 1;
    reconnectTimer = clearTimer(reconnectTimer);
    pauseTimer = clearTimer(pauseTimer);
    detachSocket();
    closedReason = reason;
    downSince ??= clock.now();
    liveSince = null;
    setStatus('closed');
  }

  function scheduleReconnect(): void {
    if (status === 'closed') return;
    reconnectTimer = clearTimer(reconnectTimer);
    const delay = feedBackoffMs(attempt, backoffInitialMs, backoffMaxMs, random);
    attempt += 1;
    reconnectTimer = clock.setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, delay);
  }

  /** The socket went away (or was given up on). `code` null = we gave up on it. */
  function handleDrop(code: number | null): void {
    if (status === 'closed') return;
    const liveFor = liveSince === null ? 0 : clock.now() - liveSince;
    detachSocket();
    if (code === FEED_CLOSE.TOO_MANY_SOCKETS) return terminate('too_many_sockets');
    if (code === FEED_CLOSE.PROTOCOL_UNSUPPORTED) return terminate('protocol_unsupported');
    if (code === FEED_CLOSE.BAD_MESSAGE) return terminate('bad_message');
    // Every other code reconnects and replays — FEED_CLOSE.IDLE (4408)
    // included: the engine heard nothing from this socket in its idle window,
    // so a client that is really there comes straight back.
    if (liveFor >= stableAfterMs) attempt = 0;
    liveSince = null;
    downSince ??= clock.now();
    if (status !== 'paused') setStatus('reconnecting');
    evaluatePause();
    scheduleReconnect();
  }

  function subscribe(fresh: boolean): void {
    const message: SubscribeMessage = {
      type: 'subscribe',
      protocol: FEED_PROTOCOL,
      view: desiredView,
      watch: desiredWatch,
      ticker: wantsTicker,
      // A resync's fresh snapshot always asks for the summary.
      summary: fresh || wantsSummary,
    };
    if (!fresh && cursor !== null) message.replay_from_seq = cursor;
    if (!send(message)) {
      // A socket that cannot take a send is not one to wait on.
      handleDrop(null);
      return;
    }
    subscribeSent = true;
    sentView = desiredView;
    sentWatch = desiredWatch;
  }

  function startPinging(): void {
    pingTimer = clearTimer(pingTimer, true);
    pingTimer = clock.setInterval(ping, pingIntervalMs);
  }

  function ping(): void {
    if (socket === null) return;
    // The timer is armed BEFORE the send, so an answer that arrives at once
    // finds it and clears it.
    if (pongTimer === null) {
      pongTimer = clock.setTimeout(() => {
        pongTimer = null;
        handleDrop(null);
      }, pongTimeoutMs);
    }
    pingSentAt = clock.now();
    send(PING);
  }

  async function connect(): Promise<void> {
    if (status === 'closed') return;
    const generation = ++connectGeneration;
    let token: string | null;
    try {
      token = await options.getToken();
    } catch (error) {
      // A newer connect — or `terminate`, which bumps the generation — owns the feed now.
      if (generation !== connectGeneration) return;
      reportError(error);
      handleDrop(null);
      return;
    }
    if (generation !== connectGeneration) return;

    const Ctor = options.WebSocket ?? platformWebSocket();
    if (Ctor === undefined) return terminate('unavailable');
    let ws: FeedSocket;
    try {
      ws = new Ctor(
        feedSocketUrl(options.engineUrl, options.engineAuctionId, options.houseId, token),
      );
    } catch (error) {
      reportError(error);
      return terminate('unavailable');
    }
    socket = ws;
    // Runs until the engine confirms the subscription (`subscribed`).
    connectTimer = clock.setTimeout(() => {
      connectTimer = null;
      if (socket === ws) handleDrop(null);
    }, connectTimeoutMs);

    ws.onopen = () => {
      if (socket !== ws) return;
      subscribe(false);
      if (socket !== ws) return; // the subscribe could not be sent
      // One ping straight away: it measures the round trip that corrects the
      // `ack` clock offset, and proves the socket before the first interval.
      ping();
      startPinging();
    };
    ws.onmessage = (event) => {
      if (socket !== ws) return;
      // Anything from the engine proves the socket is alive.
      pongTimer = clearTimer(pongTimer);
      receive(event.data);
    };
    ws.onclose = (event) => {
      if (socket !== ws) return;
      handleDrop(typeof event.code === 'number' ? event.code : null);
    };
    ws.onerror = () => {
      // A close always follows an error; the close carries the code that
      // decides what happens next. Nothing to do here.
    };
  }

  // ── messages ──
  function receive(data: unknown): void {
    if (typeof data !== 'string') return reportError(new Error('engine feed: non-text message'));
    let raw: unknown;
    try {
      raw = JSON.parse(data);
    } catch {
      return reportError(new Error('engine feed: unreadable message'));
    }
    const message = parseServerMessage(raw);
    // Unknown types are ignored, not errors — a newer engine may say more.
    if (message === null) return;
    try {
      apply(message);
    } catch (error) {
      reportError(error);
    }
  }

  function apply(message: ServerMessage): void {
    switch (message.type) {
      case 'ack': {
        role = message.role;
        const receivedAt = clock.now();
        ackSample = { serverTime: message.server_time, receivedAt };
        // Refined by half the first ping's round trip when its pong lands.
        offsetMs = message.server_time - receivedAt;
        return;
      }
      case 'subscribed': {
        noteSaleState(message.sale_state, message.current_state_seq);
        connectTimer = clearTimer(connectTimer);
        // A resync's second `subscribed` does not restart the stable period.
        liveSince ??= clock.now();
        downSince = null;
        pauseTimer = clearTimer(pauseTimer);
        setStatus('live');
        return;
      }
      case 'summary': {
        noteSeq(message.seq);
        const changes: FeedLotChange[] = [];
        for (const raw of message.lots as unknown[]) {
          const line = readSummaryLine(raw);
          if (line === null) continue;
          const prev = lots.get(line.lot_id);
          // A line never displaces a view at least as new; it replaces an
          // older one whole — the fields it lacks may have moved with it.
          if (prev !== undefined && !isNewerSeq(line.seq, prev.seq)) continue;
          lots.set(line.lot_id, line);
          changes.push({ lot: line, prev });
        }
        if (changes.length > 0) emit('lots', changes);
        return;
      }
      case 'lot': {
        noteSeq(message.seq);
        const view = readLotView(message.lot);
        if (view === null) return reportError(new Error('engine feed: malformed lot view'));
        noteSeq(view.seq);
        const prev = lots.get(view.lot_id);
        const newer =
          prev === undefined ||
          isNewerSeq(view.seq, prev.seq) ||
          // The same change, in full, after its summary line.
          (view.seq === prev.seq && prev.detail === 'summary');
        if (!newer) return;
        lots.set(view.lot_id, view);
        emit('lots', [{ lot: view, prev }]);
        return;
      }
      case 'you': {
        const standing = readYou(message as unknown as Record<string, unknown>);
        if (standing === null) return reportError(new Error('engine feed: malformed you'));
        noteSeq(standing.seq);
        const prev = yours.get(standing.lot_id);
        if (prev !== undefined && !isNewerSeq(standing.seq, prev.seq)) return;
        yours.set(standing.lot_id, standing);
        emit('you', standing, prev);
        return;
      }
      case 'ticker': {
        const entry = readTicker(message as unknown as Record<string, unknown>);
        if (entry === null) return reportError(new Error('engine feed: malformed ticker'));
        noteSeq(entry.seq);
        // Keyed on the whole line, so only a true replay is dropped — never a
        // second, different line the same change produced.
        const key = `${entry.seq}|${entry.lot_id}|${String(entry.amount_cents)}|${entry.at}`;
        if (seenTicker.add(key)) emit('ticker', entry);
        return;
      }
      case 'sale': {
        noteSeq(message.seq);
        noteSaleState(message.state, message.seq);
        return;
      }
      case 'event': {
        if (isSeq(message.sequence_num)) noteSeq(message.sequence_num);
        if (typeof message.event_id !== 'string') return;
        if (seenEvents.add(message.event_id)) emit('event', message);
        return;
      }
      case 'resync': {
        // The replay point is gone (or the engine rebuilt its state): forget
        // the lots and take a fresh snapshot on this socket. Standings are
        // kept — the engine re-sends them, and a newer one replaces each.
        lots.clear();
        cursor = null;
        subscribe(true);
        return;
      }
      case 'pong': {
        if (pingSentAt === null) return;
        const rtt = clock.now() - pingSentAt;
        pingSentAt = null;
        // The `ack` left the engine half a round trip before it arrived. One
        // refinement per socket; later pongs only prove the socket alive.
        if (ackSample !== null && rtt >= 0) {
          offsetMs = ackSample.serverTime + rtt / 2 - ackSample.receivedAt;
          ackSample = null;
        }
        return;
      }
      case 'error': {
        if (message.code === 'protocol_unsupported') return terminate('protocol_unsupported');
        reportError(
          new Error(`engine feed error ${String(message.code)}: ${String(message.message)}`),
        );
        return;
      }
    }
  }

  function noteSaleState(state: unknown, seq: string): void {
    if (!isSaleState(state)) return;
    if (saleSeq !== null && !isNewerSeq(seq, saleSeq)) return;
    saleSeq = seq;
    if (state === saleState) return;
    saleState = state;
    emit('sale', state);
  }

  // ── interest ──
  function updateInterest(kind: 'view' | 'watch', lotIds: readonly string[]): InterestUpdate {
    const cap = kind === 'view' ? FEED_LIMITS.MAX_VIEW_LOTS : FEED_LIMITS.MAX_WATCH_LOTS;
    const { lots: next, dropped } = normalizeInterest(lotIds, cap);
    const before = kind === 'view' ? desiredView : desiredWatch;
    const changed = !sameList(before, next);
    if (kind === 'view') desiredView = next;
    else desiredWatch = next;
    // Not subscribed yet: the next `subscribe` carries it.
    if (status !== 'closed' && subscribeSent) {
      const sent = kind === 'view' ? sentView : sentWatch;
      if (!sameList(sent, next) && send({ type: kind, lots: next })) {
        if (kind === 'view') sentView = next;
        else sentWatch = next;
      }
    }
    return { changed, lots: next, dropped };
  }

  // Go. The first connect waits for nothing; `paused` is judged from now.
  evaluatePause();
  void connect();

  return {
    on(event, listener) {
      listeners[event].add(listener);
      return () => {
        listeners[event].delete(listener);
      };
    },
    setView: (lotIds) => updateInterest('view', lotIds),
    setWatch: (lotIds) => updateInterest('watch', lotIds),
    lot: (lotId) => lots.get(lotId),
    lots: () => lots,
    you: (lotId) => yours.get(lotId),
    yours: () => yours,
    status: statusInfo,
    saleState: () => saleState,
    role: () => role,
    serverOffsetMs: () => offsetMs,
    close: () => terminate('closed_by_caller'),
  };
}
